import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cartStore } from '../utils/cartStore';
import {
  buildProductGallery,
  colorOptionsAreGalleryViews,
  colorOptionsHaveSwatches,
  formatSizeChartText,
  isLongProductColorLabel,
  isSizeChartImageUrl,
  parseProductStock,
  parseProductTabs,
  parseSizeChartTable,
  parseSizePriceTiers,
  parseColorPriceTiers,
  resolveColorForGalleryIndex,
  resolveColorGalleryIndex,
  resolveVariantPrice,
  type ProductColorOption,
  type SizePriceTier,
  type ColorPriceTier,
} from '../utils/productDetailHelpers';
import { carouselSlideAnimationClass, normalizeCarouselSlideAnimation, carouselNavDirectionClass } from '../utils/blockContentCodec';
import { useCarouselIndexTransition } from '../utils/useCarouselIndexTransition';
import { resolveMediaSrc } from '../utils/mediaEmbed';
import {
  displayCommercePrice,
  parseCommercePrice,
} from '../utils/commerceCurrency';
import {
  isInternalSiteLink,
  isSelfCollectionLink,
  normalizeInternalPath,
} from '../utils/collectionDetailMode';
import type { TabItem } from '../utils/blockContentCodec';
import { PriceTaxLabelButton } from './PriceTaxLabelButton';
import type { TaxModalConfig } from '../utils/taxInfoModal';
/**
 * All PDP BEM (gallery, size chips, buy box, sticky ATC) lives in this
 * co-located sheet so Vite cannot orphan it inside SiteRenderer-*.css.
 * Do NOT import SiteRenderer.css / PublicSitePage.css here — that merged
 * product rules into the shared SiteRenderer chunk and left PublicSitePage-*.css
 * as a ~1KB lang-switcher stub on storefront PDP loads.
 */
import './ProductDetailView.css';

function pathsEqual(a: string, b: string): boolean {
  return normalizeInternalPath(a) === normalizeInternalPath(b);
}

export type ProductDetailViewProps = {
  name: string;
  priceRaw: string;
  compareAtPriceRaw?: string;
  shortDescription?: string;
  description: string;
  role: string;
  category: string;
  link: string;
  imageUrl: string;
  galleryImages?: string;
  galleryAnimation?: string;
  /** Touch swipe on multi-image PDP gallery. Default true. */
  gallerySwipeable?: boolean;
  /**
   * When true, gallery slide/swipe/autoplay updates selected color (and price tiers).
   * When false (default), gallery is independent; color→image still works.
   */
  galleryOptionsSync?: boolean;
  sizeOptions: string[];
  colorOptions: ProductColorOption[];
  /** Size→price groups; array or serialized multiline string. */
  sizePriceTiers?: SizePriceTier[] | string;
  /** Color→price groups; array or serialized multiline string. */
  colorPriceTiers?: ColorPriceTier[] | string;
  tabContent?: string;
  sizeChart?: string;
  sku?: string;
  stockRaw?: string;
  isProductCollection: boolean;
  /** When true, render inquiry CTA (no cart) and Service JSON-LD. */
  isServiceCollection?: boolean;
  collectionSlug?: string;
  itemSlug?: string;
  inquiryHref?: string;
  collectionLabel: string;
  collectionIndexHref: string;
  cartHref: string;
  currencySymbol: string;
  cartKey: string;
  productId: string;
  /**
   * When true, pin add-to-cart to the bottom of the viewport on mobile.
   * Default false: ATC stays in document flow below quantity.
   */
  stickyAddToCart?: boolean;
  /** Site language for locale number format + auto currency position. */
  priceLocale?: string;
  /** Shop preference: auto | left | right (default auto). */
  currencyPosition?: 'auto' | 'left' | 'right';
  /** Resolved tax hint beside the amount (empty = hide). */
  priceTaxLabel?: string;
  /** Modal copy when tax label is a button (shop / block / product). */
  taxModalConfig?: TaxModalConfig | null;
  /** Optional 30-day low price raw string (hidden in modal if empty). */
  priceLow30dRaw?: string;
};

export function ProductDetailView({
  name,
  priceRaw,
  compareAtPriceRaw = '',
  shortDescription = '',
  description,
  role,
  category,
  link,
  imageUrl,
  galleryImages,
  galleryAnimation = '',
  gallerySwipeable = true,
  galleryOptionsSync = false,
  sizeOptions,
  colorOptions,
  sizePriceTiers: sizePriceTiersProp,
  colorPriceTiers: colorPriceTiersProp,
  tabContent = '',
  sizeChart = '',
  sku = '',
  stockRaw = '',
  isProductCollection,
  isServiceCollection = false,
  collectionSlug = '',
  itemSlug = '',
  inquiryHref = '',
  collectionLabel,
  collectionIndexHref,
  cartHref,
  currencySymbol,
  cartKey,
  productId,
  stickyAddToCart = false,
  priceLocale = '',
  currencyPosition = 'auto',
  priceTaxLabel = '',
  taxModalConfig = null,
  priceLow30dRaw = '',
}: ProductDetailViewProps) {
  const { t } = useTranslation();
  const safeSizeOptions = Array.isArray(sizeOptions) ? sizeOptions : [];
  const safeColorOptions = Array.isArray(colorOptions) ? colorOptions : [];
  const sizePriceTiers = useMemo(() => {
    if (Array.isArray(sizePriceTiersProp)) {
      return sizePriceTiersProp.map((tier) => ({
        sizes: Array.isArray(tier?.sizes) ? tier.sizes.map(String) : [],
        price: String(tier?.price ?? ''),
      }));
    }
    return parseSizePriceTiers(sizePriceTiersProp);
  }, [sizePriceTiersProp]);
  const colorPriceTiers = useMemo(() => {
    if (Array.isArray(colorPriceTiersProp)) {
      return colorPriceTiersProp.map((tier) => ({
        colors: Array.isArray(tier?.colors) ? tier.colors.map(String) : [],
        price: String(tier?.price ?? ''),
      }));
    }
    return parseColorPriceTiers(colorPriceTiersProp);
  }, [colorPriceTiersProp]);
  const gallery = useMemo(
    () => buildProductGallery(imageUrl, galleryImages, safeColorOptions),
    [imageUrl, galleryImages, safeColorOptions],
  );
  const hasGallery = gallery.length > 0;
  const initialColorIndex = safeColorOptions.length > 0 ? 0 : -1;
  const initialColor = safeColorOptions[0]?.label || '';
  const initialImage = resolveColorGalleryIndex(initialColor, safeColorOptions, gallery, 0) ?? 0;

  const [activeImage, setActiveImage] = useState(initialImage);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(safeSizeOptions[0] || '');
  const [colorIndex, setColorIndex] = useState(initialColorIndex);
  const [addedFlash, setAddedFlash] = useState(false);
  const [priceFlash, setPriceFlash] = useState(false);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxEntered, setLightboxEntered] = useState(false);
  const priceFlashReady = useRef(false);
  const lightboxCloseRef = useRef<HTMLButtonElement | null>(null);
  const lightboxThumbsRef = useRef<HTMLDivElement | null>(null);
  const lightboxTouchStartX = useRef<number | null>(null);

  const color = colorIndex >= 0 ? (safeColorOptions[colorIndex]?.label || '') : '';
  const selectedColorOption = colorIndex >= 0 ? (safeColorOptions[colorIndex] || null) : null;
  const colorDisplay =
    color
    || (colorIndex >= 0
      ? t('project.productEditorColorDefaultName', { n: colorIndex + 1, defaultValue: `Color ${colorIndex + 1}` })
      : '');

  const resolvedPriceRaw = useMemo(
    () => resolveVariantPrice({
      basePrice: priceRaw,
      size,
      color: selectedColorOption,
      sizePriceTiers,
      colorPriceTiers,
    }),
    [priceRaw, size, selectedColorOption, sizePriceTiers, colorPriceTiers],
  );
  const priceNumber = parseCommercePrice(resolvedPriceRaw);
  const hasLocale = Boolean(String(priceLocale || '').trim());
  const moneyOpts =
    hasLocale || currencyPosition !== 'auto'
      ? { locale: hasLocale ? priceLocale : undefined, currencyPosition }
      : undefined;
  const priceLabel = displayCommercePrice(resolvedPriceRaw, currencySymbol, moneyOpts);
  const compareAtLabel = displayCommercePrice(compareAtPriceRaw, currencySymbol, moneyOpts);
  const taxHint = String(priceTaxLabel || '').trim();
  const compareAtNumber = parseCommercePrice(compareAtPriceRaw);
  const showCompareAt = Boolean(compareAtLabel && compareAtNumber > priceNumber && priceNumber > 0);
  const priceLow30dLabel = displayCommercePrice(priceLow30dRaw, currencySymbol, moneyOpts);
  const taxModalPrices = taxModalConfig
    ? {
        currentPrice: priceLabel || undefined,
        taxNote: taxHint || undefined,
        compareAtPrice: showCompareAt ? compareAtLabel : undefined,
        priceLow30d: priceLow30dLabel || undefined,
      }
    : undefined;
  const stock = parseProductStock(stockRaw);
  const outOfStock = stock === 0;
  const useSwatches = colorOptionsHaveSwatches(safeColorOptions);
  const colorsAreGalleryViews = colorOptionsAreGalleryViews(safeColorOptions);
  const descTitle = isServiceCollection ? t('site.serviceDescription') : t('site.productDescription');
  const tabs: TabItem[] = parseProductTabs(tabContent, description, descTitle);
  const showInlineDescription = tabs.length === 0 && Boolean(description.trim());
  const sizeChartIsImage = isSizeChartImageUrl(sizeChart);
  const sizeChartText = sizeChartIsImage ? '' : formatSizeChartText(sizeChart);
  const sizeChartTable = sizeChartIsImage ? null : parseSizeChartTable(sizeChart);
  const selfLink = Boolean(
    link
    && collectionSlug
    && itemSlug
    && isSelfCollectionLink(link, collectionSlug, itemSlug),
  );
  const visibleLink = link && !selfLink ? link : '';
  const showInquiryCta = isServiceCollection && Boolean(inquiryHref);
  const linkDuplicatesInquiry =
    Boolean(visibleLink && inquiryHref && pathsEqual(visibleLink, inquiryHref));

  const productIdentity = `${collectionSlug}:${itemSlug}:${imageUrl}:${galleryImages || ''}`;
  const optionsIdentity = `${safeSizeOptions.join('\u0001')}::${safeColorOptions.map((c) => `${c.label}|${c.hex || ''}|${c.imageUrl || ''}|${c.price || ''}`).join('\u0001')}::${sizePriceTiers.map((tier) => `${tier.sizes.join(',')}|${tier.price}`).join('\u0002')}::${colorPriceTiers.map((tier) => `${tier.colors.join(',')}|${tier.price}`).join('\u0002')}`;

  useEffect(() => {
    const nextColor = safeColorOptions[0]?.label || '';
    const nextGallery = buildProductGallery(imageUrl, galleryImages, safeColorOptions);
    const nextImage = resolveColorGalleryIndex(nextColor, safeColorOptions, nextGallery, 0) ?? 0;
    setQty(1);
    setSize(safeSizeOptions[0] || '');
    setColorIndex(safeColorOptions.length > 0 ? 0 : -1);
    setActiveImage(nextImage);
    setAddedFlash(false);
    setPriceFlash(false);
    priceFlashReady.current = false;
    setSizeChartOpen(false);
    setActiveTab(0);
    setLightboxOpen(false);
    setLightboxEntered(false);
    // Reset only when the product identity changes — not on every colorOptions ref churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional product-scoped reset
  }, [productIdentity]);

  useEffect(() => {
    if (!priceFlashReady.current) {
      priceFlashReady.current = true;
      return;
    }
    setPriceFlash(true);
    const timer = window.setTimeout(() => setPriceFlash(false), 560);
    return () => window.clearTimeout(timer);
  }, [resolvedPriceRaw]);

  useEffect(() => {
    if (!safeSizeOptions.length) {
      setSize('');
      return;
    }
    if (!safeSizeOptions.includes(size)) setSize(safeSizeOptions[0]);
  }, [size, safeSizeOptions, optionsIdentity]);

  useEffect(() => {
    if (!safeColorOptions.length) {
      setColorIndex(-1);
      return;
    }
    if (colorIndex < 0 || colorIndex >= safeColorOptions.length) {
      setColorIndex(0);
      const next = safeColorOptions[0];
      const idx = resolveColorGalleryIndex(next?.label || '', safeColorOptions, gallery, 0);
      if (idx != null) setActiveImage(idx);
    }
  }, [colorIndex, safeColorOptions, gallery, optionsIdentity]);

  useEffect(() => {
    if (!gallery.length) {
      setActiveImage(0);
      return;
    }
    if (activeImage >= gallery.length) setActiveImage(gallery.length - 1);
  }, [gallery, activeImage]);

  const selectColorAt = (index: number) => {
    if (index < 0 || index >= safeColorOptions.length) return;
    setColorIndex(index);
    const opt = safeColorOptions[index];
    const idx = resolveColorGalleryIndex(opt.label, safeColorOptions, gallery, index);
    if (idx != null) setActiveImage(idx);
  };

  const selectGalleryIndex = (index: number) => {
    if (index < 0 || index >= gallery.length) return;
    setActiveImage(index);
    // Optional: gallery → color (and thus size/price tiers). Off by default for cart clarity.
    // Color → gallery always runs via selectColorAt regardless of this flag.
    if (!galleryOptionsSync) return;
    const match = resolveColorForGalleryIndex(index, safeColorOptions, gallery);
    if (match == null) return;
    const matchIdx = safeColorOptions.findIndex((c, i) => {
      if (c.label && c.label === match) return true;
      return !c.label && !match && i === index;
    });
    if (matchIdx >= 0) setColorIndex(matchIdx);
  };

  const addToCart = () => {
    if (!isProductCollection || priceNumber <= 0 || outOfStock) return;
    const colorLabel = colorDisplay || color;
    const suffix = [size, colorLabel].filter(Boolean).join('-');
    const id = suffix ? `${productId}:${suffix.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : productId;
    const options = [size, colorLabel].filter(Boolean);
    const thumb = gallery[activeImage] || imageUrl || undefined;
    cartStore.add(
      cartKey,
      { id, name, price: priceNumber, imageUrl: thumb || undefined, options: options.length ? options : undefined },
      qty,
    );
    cartStore.requestOpen(cartKey);
    setAddedFlash(true);
    window.setTimeout(() => setAddedFlash(false), 2400);
  };

  useEffect(() => {
    if (isServiceCollection) {
      const ld = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name,
        description: description || undefined,
        category: category || undefined,
        provider: undefined,
      };
      let el = document.getElementById('ld-json-product') as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = 'ld-json-product';
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(ld);
      return () => {
        el?.remove();
      };
    }
    if (!isProductCollection || priceNumber <= 0) return;
    const mainImage = gallery[activeImage] || imageUrl;
    const availability =
      stock === 0
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock';
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description: description || undefined,
      image: mainImage || undefined,
      category: category || undefined,
      sku: sku.trim() || undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: currencySymbol === '€' ? 'EUR' : currencySymbol === '£' ? 'GBP' : currencySymbol === '$' ? 'USD' : 'EUR',
        price: priceNumber.toFixed(2),
        availability,
      },
    };
    let el = document.getElementById('ld-json-product') as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = 'ld-json-product';
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => {
      el?.remove();
    };
  }, [isServiceCollection, isProductCollection, name, description, imageUrl, gallery, activeImage, category, priceNumber, currencySymbol, sku, stock]);

  const safeImageIndex = Math.min(activeImage, Math.max(0, gallery.length - 1));
  const currentSrc = gallery[safeImageIndex] ? resolveMediaSrc(gallery[safeImageIndex]) : '';
  const galleryAnim = normalizeCarouselSlideAnimation(galleryAnimation);
  // Multi-image PDPs always use the carousel slide DOM so non-default presets
  // (cube/flip/…) and Standard fade share one path — never a plain <img> swap
  // that silently ignores galleryAnimation from the public API.
  const useGalleryCarousel = gallery.length > 1;
  const useGalleryMotion = useGalleryCarousel && galleryAnim !== 'default';
  const {
    exiting: galleryExiting,
    goTo: goToGalleryIndex,
    goNext: goNextGallery,
    goPrev: goPrevGallery,
    transitionGen: galleryTransitionGen,
    navDir: galleryNavDir,
  } = useCarouselIndexTransition(
    safeImageIndex,
    (next) => {
      selectGalleryIndex(next);
    },
    galleryAnim,
    gallery.length,
  );

  const selectGalleryIndexAnimated = (index: number) => {
    if (index < 0 || index >= gallery.length) return;
    if (useGalleryCarousel) {
      goToGalleryIndex(index);
      return;
    }
    selectGalleryIndex(index);
  };

  const goPrevImage = () => {
    if (gallery.length < 2) return;
    if (useGalleryCarousel) {
      goPrevGallery();
      return;
    }
    selectGalleryIndexAnimated((safeImageIndex - 1 + gallery.length) % gallery.length);
  };

  const goNextImage = () => {
    if (gallery.length < 2) return;
    if (useGalleryCarousel) {
      goNextGallery();
      return;
    }
    selectGalleryIndexAnimated((safeImageIndex + 1) % gallery.length);
  };

  const openLightbox = (index?: number) => {
    if (!hasGallery) return;
    if (typeof index === 'number' && index >= 0 && index < gallery.length) {
      selectGalleryIndex(index);
    }
    setLightboxEntered(false);
    setLightboxOpen(true);
    setGalleryPaused(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxEntered(false);
    setGalleryPaused(false);
  };

  const onLightboxBackdropClick = (ev: ReactMouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget) closeLightbox();
  };

  const onLightboxTouchStart = (e: TouchEvent) => {
    if (gallery.length < 2) return;
    lightboxTouchStartX.current = e.touches[0].clientX;
  };

  const onLightboxTouchEnd = (e: TouchEvent) => {
    if (gallery.length < 2 || lightboxTouchStartX.current === null) {
      lightboxTouchStartX.current = null;
      return;
    }
    const delta = e.changedTouches[0].clientX - lightboxTouchStartX.current;
    lightboxTouchStartX.current = null;
    if (Math.abs(delta) > 48) (delta > 0 ? goPrevImage() : goNextImage());
  };

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const enterFrame = window.requestAnimationFrame(() => setLightboxEntered(true));
    const focusTimer = window.setTimeout(() => lightboxCloseRef.current?.focus(), 40);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        closeLightbox();
        return;
      }
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        goPrevImage();
        return;
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        goNextImage();
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(enterFrame);
      window.clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lightbox chrome only
  }, [lightboxOpen, gallery.length, safeImageIndex]);

  useEffect(() => {
    if (!lightboxOpen || gallery.length < 2) return;
    const root = lightboxThumbsRef.current;
    if (!root) return;
    const active = root.querySelector<HTMLElement>('.public-site__product-lightbox__thumb--active');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [lightboxOpen, safeImageIndex, gallery.length]);

  const touchStartX = useRef<number | null>(null);
  const swipeEnabled = gallerySwipeable !== false && gallery.length > 1;
  const onGalleryTouchStart = (e: TouchEvent) => {
    if (!swipeEnabled) return;
    touchStartX.current = e.touches[0].clientX;
    if (useGalleryMotion) setGalleryPaused(true);
  };
  const onGalleryTouchEnd = (e: TouchEvent) => {
    if (!swipeEnabled || touchStartX.current === null) {
      touchStartX.current = null;
      if (useGalleryMotion) setGalleryPaused(false);
      return;
    }
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    // Same threshold as SiteRenderer carousel swipe.
    if (Math.abs(delta) > 40) (delta > 0 ? goPrevImage() : goNextImage());
    touchStartX.current = null;
    if (useGalleryMotion) setGalleryPaused(false);
  };

  // Non-default gallery animations mirror SiteRenderer carousel autoplay.
  useEffect(() => {
    if (!useGalleryMotion || galleryPaused || gallery.length < 2) return;
    const id = window.setInterval(() => {
      goToGalleryIndex((safeImageIndex + 1) % gallery.length);
    }, 5500);
    return () => window.clearInterval(id);
  }, [useGalleryMotion, galleryPaused, gallery.length, safeImageIndex, goToGalleryIndex]);

  return (
    <article className={`public-site__product${hasGallery ? ' public-site__product--split' : ''}${isProductCollection ? ' public-site__product--shop' : ''}${isServiceCollection ? ' public-site__product--service' : ''}${stickyAddToCart ? ' public-site__product--sticky-atc' : ''}`}>
      <a href={collectionIndexHref} className="public-site__product-back">← {collectionLabel}</a>

      {hasGallery ? (
        <div className="public-site__product-media">
          <div
            className="public-site__product-gallery"
            onMouseEnter={useGalleryMotion ? () => setGalleryPaused(true) : undefined}
            onMouseLeave={useGalleryMotion ? () => setGalleryPaused(false) : undefined}
            onFocus={useGalleryMotion ? () => setGalleryPaused(true) : undefined}
            onBlur={useGalleryMotion ? () => setGalleryPaused(false) : undefined}
          >
            <div
              className={`public-site__product-gallery-main${swipeEnabled ? ' public-site__product-gallery-main--swipeable' : ''}${hasGallery ? ' public-site__product-gallery-main--zoomable' : ''}`}
              onTouchStart={swipeEnabled ? onGalleryTouchStart : undefined}
              onTouchEnd={swipeEnabled ? onGalleryTouchEnd : undefined}
            >
              {gallery.length > 1 ? (
                <>
                  <button type="button" className="public-site__product-gallery-nav public-site__product-gallery-nav--prev" onClick={goPrevImage} aria-label={t('common.prev')}>‹</button>
                  <button type="button" className="public-site__product-gallery-nav public-site__product-gallery-nav--next" onClick={goNextImage} aria-label={t('common.next')}>›</button>
                </>
              ) : null}
              <button
                type="button"
                className="public-site__product-gallery-expand"
                onClick={() => openLightbox(safeImageIndex)}
                aria-label={t('site.productGalleryExpand', { defaultValue: 'View larger image' })}
              >
                {t('site.productGalleryExpandShort', { defaultValue: 'Enlarge' })}
              </button>
              {useGalleryCarousel ? (
                <div
                  key={`pdp-gallery-anim-${galleryAnim}`}
                  className={`public-site__product-gallery-main--animated site-carousel site-carousel--gallery site-carousel--fit-cover ${carouselSlideAnimationClass(galleryAnim)} ${carouselNavDirectionClass(galleryNavDir)}`}
                  data-gallery-animation={galleryAnim}
                  data-gallery-nav-dir={galleryNavDir}
                >
                  <div className="site-carousel__frame">
                    {gallery.map((src, i) => {
                      const isActive = i === safeImageIndex;
                      const isExiting = galleryExiting === i;
                      return (
                        <div
                          key={`${src}-${i}-${isActive || isExiting ? galleryTransitionGen : 'idle'}`}
                          aria-hidden={!isActive}
                          className={`site-carousel__slide site-carousel__slide--media${isActive ? ' site-carousel__slide--active' : ''}${isExiting ? ' site-carousel__slide--exiting' : ''}`}
                        >
                          <button
                            type="button"
                            className="public-site__product-gallery-open"
                            onClick={() => openLightbox(i)}
                            aria-label={t('site.productGalleryExpand', { defaultValue: 'View larger image' })}
                          >
                            <img src={resolveMediaSrc(src)} alt={name} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="public-site__product-gallery-open"
                  onClick={() => openLightbox(0)}
                  aria-label={t('site.productGalleryExpand', { defaultValue: 'View larger image' })}
                >
                  <img src={currentSrc} alt={name} />
                </button>
              )}
            </div>
            {gallery.length > 1 ? (
              <div className="public-site__product-gallery-thumbs" role="tablist" aria-label={t('site.productGallery')}>
                {gallery.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    role="tab"
                    aria-selected={i === safeImageIndex}
                    className={`public-site__product-gallery-thumb${i === safeImageIndex ? ' public-site__product-gallery-thumb--active' : ''}`}
                    onClick={() => selectGalleryIndexAnimated(i)}
                    onDoubleClick={() => openLightbox(i)}
                  >
                    <img src={resolveMediaSrc(src)} alt="" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="public-site__product-copy">
        <header className="public-site__product-head">
          <h1 className="public-site__product-title">{name}</h1>
          {role ? (
            <p className="public-site__product-role public-site__product-brand">{role}</p>
          ) : null}
          {priceLabel ? (
            <p className={`public-site__product-price${priceFlash ? ' public-site__product-price--flash' : ''}`}>
              <span className="public-site__product-price-now">
                <span className="public-site__product-price-amount">{priceLabel}</span>
                {taxHint && taxModalConfig ? (
                  <PriceTaxLabelButton
                    label={taxHint}
                    className="public-site__product-price-tax"
                    config={taxModalConfig}
                    prices={taxModalPrices}
                  />
                ) : taxHint ? (
                  <span className="public-site__product-price-tax">{taxHint}</span>
                ) : null}
              </span>
              {showCompareAt ? <span className="public-site__product-price-was">{compareAtLabel}</span> : null}
            </p>
          ) : null}
          {sku.trim() ? <p className="public-site__product-sku">{t('site.productSku')}: {sku.trim()}</p> : null}
          {outOfStock ? <p className="public-site__product-stock public-site__product-stock--out">{t('site.outOfStock')}</p> : null}
          {category ? <p className="public-site__product-category">{category}</p> : null}
        </header>
        {shortDescription.trim() ? (
          <p className="public-site__product-lede">{shortDescription.trim()}</p>
        ) : null}

        {showInlineDescription ? (
          <div className="public-site__product-desc">
            <h2 className="public-site__product-desc-title">{descTitle}</h2>
            <div className="public-site__product-body">{description}</div>
          </div>
        ) : null}

        {showInquiryCta ? (
          <div className="public-site__product-actions public-site__product-actions--service">
            <a href={inquiryHref} className="public-site__product-inquire">
              {t('site.inquireAboutService')}
            </a>
          </div>
        ) : null}

        {isProductCollection && (priceNumber > 0 || safeSizeOptions.length > 0 || safeColorOptions.length > 0) ? (
          <div className={`public-site__product-buy${stickyAddToCart ? ' public-site__product-buy--sticky-atc' : ''}`}>
            {safeSizeOptions.length > 0 ? (
              <div className="public-site__product-option">
                <span className="public-site__product-option-label">{t('site.productSize')}</span>
                <div className="public-site__product-sizes" role="radiogroup" aria-label={t('site.productSize')}>
                  {safeSizeOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      role="radio"
                      aria-checked={size === opt}
                      title={opt}
                      className={`public-site__product-size${size === opt ? ' public-site__product-size--active' : ''}`}
                      onClick={() => setSize(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {sizeChart ? (
              <div className="public-site__product-size-chart">
                <button
                  type="button"
                  className="public-site__product-size-chart-toggle"
                  aria-expanded={sizeChartOpen}
                  onClick={() => setSizeChartOpen((v) => !v)}
                >
                  <span>{t('site.productSizeChart')}</span>
                  <span className="public-site__product-size-chart-toggle-icon" aria-hidden>{sizeChartOpen ? '▾' : '▸'}</span>
                </button>
                {sizeChartOpen ? (
                  <div className="public-site__product-size-chart-panel">
                    {sizeChartIsImage ? (
                      <img src={resolveMediaSrc(sizeChart)} alt={t('site.productSizeChart')} />
                    ) : sizeChartTable ? (
                      <div className="public-site__size-chart-table-wrap">
                        <table className="public-site__size-chart-table">
                          <thead>
                            <tr>
                              {sizeChartTable.headers.map((cell, i) => (
                                <th key={i} className="public-site__size-chart-table__th">{cell}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sizeChartTable.rows.map((row, ri) => (
                              <tr
                                key={ri}
                                className={size && row[0]?.toLowerCase() === size.toLowerCase() ? 'public-site__size-chart-table__row--active' : ''}
                              >
                                {row.map((cell, ci) => (
                                  <td key={ci} className={ci === 0 ? 'public-site__size-chart-table__size' : 'public-site__size-chart-table__cell'}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <pre className="public-site__product-size-chart-text">{sizeChartText}</pre>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {safeColorOptions.length > 0 ? (
              <div className={`public-site__product-option${colorsAreGalleryViews ? ' public-site__product-option--gallery-views' : ''}`}>
                <span className="public-site__product-option-label">{t('site.productColor')}</span>
                <div className="public-site__product-colors" role="radiogroup" aria-label={t('site.productColor')}>
                  {safeColorOptions.map((opt, index) => (
                    <button
                      key={`${opt.label || 'color'}-${index}`}
                      type="button"
                      role="radio"
                      aria-checked={colorIndex === index}
                      title={opt.label || t('project.productEditorColorDefaultName', { n: index + 1, defaultValue: `Color ${index + 1}` })}
                      className={`public-site__product-color${colorIndex === index ? ' public-site__product-color--active' : ''}${!useSwatches || !opt.hex ? ' public-site__product-color--text' : ''}${isLongProductColorLabel(opt.label || '') ? ' public-site__product-color--long' : ''}`}
                      style={opt.hex ? { ['--product-color-swatch' as string]: opt.hex } : undefined}
                      onClick={() => selectColorAt(index)}
                    >
                      {opt.hex ? <span className="public-site__product-color-swatch" aria-hidden="true" /> : null}
                      <span className="public-site__product-color-label">
                        {opt.label || t('project.productEditorColorDefaultName', { n: index + 1, defaultValue: `Color ${index + 1}` })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {priceNumber > 0 ? (
              <>
                <div className="public-site__product-qty" role="group" aria-label={t('site.productQuantity')}>
                  <span className="public-site__product-option-label">{t('site.productQuantity')}</span>
                  <div className="public-site__product-qty-controls">
                    <button type="button" className="public-site__product-qty-btn" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="−">−</button>
                    <span className="public-site__product-qty-value">{qty}</span>
                    <button type="button" className="public-site__product-qty-btn" onClick={() => setQty((q) => Math.min(99, q + 1))} aria-label="+">+</button>
                  </div>
                </div>

                <div className={`public-site__product-actions public-site__product-actions--buy${stickyAddToCart ? ' public-site__product-actions--sticky' : ''}`}>
                  {outOfStock ? (
                    <button type="button" className="public-site__product-add public-site__product-add--disabled" disabled>
                      {t('site.outOfStock')}
                    </button>
                  ) : (
                    <button type="button" className="public-site__product-add" onClick={addToCart}>
                      {t('site.addToCart')}
                    </button>
                  )}
                  <a href={cartHref} className="public-site__product-cart-link">{t('site.viewCart')}</a>
                  <p
                    className={`public-site__product-added${addedFlash ? ' public-site__product-added--visible' : ''}`}
                    role="status"
                    aria-live="polite"
                    aria-hidden={addedFlash ? undefined : true}
                  >
                    {t('site.addedToCart')}
                  </p>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {visibleLink && !linkDuplicatesInquiry ? (
          <p className="public-site__product-link">
            <a
              href={visibleLink}
              {...(isInternalSiteLink(visibleLink)
                ? {}
                : { target: '_blank', rel: 'noopener noreferrer' })}
            >
              {isInternalSiteLink(visibleLink)
                ? t('site.openLinkedPage')
                : t('site.viewExternalLink')}
            </a>
          </p>
        ) : null}
      </div>

      {tabs.length > 0 ? (
        <div className="public-site__product-tabs">
          <div className="public-site__product-tabs__bar" role="tablist">
            {tabs.map((tab, i) => (
              <button
                key={`${tab.label}-${i}`}
                type="button"
                role="tab"
                aria-selected={i === activeTab}
                className={`public-site__product-tabs__tab${i === activeTab ? ' public-site__product-tabs__tab--active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="public-site__product-tabs__panel" role="tabpanel">
            {tabs[activeTab]?.content}
          </div>
        </div>
      ) : null}

      {lightboxOpen && hasGallery
        ? createPortal(
          <div
            className={`public-site__product-lightbox${lightboxEntered ? ' public-site__product-lightbox--entered' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={t('site.productGalleryExpand', { defaultValue: 'View larger image' })}
            onClick={onLightboxBackdropClick}
          >
            <button
              ref={lightboxCloseRef}
              type="button"
              className="public-site__product-lightbox__close"
              onClick={closeLightbox}
              aria-label={t('common.close', { defaultValue: 'Close' })}
            >
              <span className="public-site__product-lightbox__close-icon" aria-hidden="true">×</span>
              <span className="public-site__product-lightbox__close-hint" aria-hidden="true">Esc</span>
            </button>

            <div
              className="public-site__product-lightbox__stage"
              onClick={onLightboxBackdropClick}
              onTouchStart={onLightboxTouchStart}
              onTouchEnd={onLightboxTouchEnd}
            >
              {gallery.length > 1 ? (
                <button
                  type="button"
                  className="public-site__product-lightbox__nav public-site__product-lightbox__nav--prev"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    goPrevImage();
                  }}
                  aria-label={t('common.prev')}
                >
                  ‹
                </button>
              ) : null}

              <figure
                className="public-site__product-lightbox__figure"
                onClick={(ev) => ev.stopPropagation()}
              >
                <img
                  key={currentSrc}
                  className="public-site__product-lightbox__image"
                  src={currentSrc}
                  alt={name}
                  draggable={false}
                />
              </figure>

              {gallery.length > 1 ? (
                <button
                  type="button"
                  className="public-site__product-lightbox__nav public-site__product-lightbox__nav--next"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    goNextImage();
                  }}
                  aria-label={t('common.next')}
                >
                  ›
                </button>
              ) : null}
            </div>

            <div
              className="public-site__product-lightbox__footer"
              onClick={(ev) => ev.stopPropagation()}
            >
              <p className="public-site__product-lightbox__caption">
                <span className="public-site__product-lightbox__caption-name">{name}</span>
                {gallery.length > 1 ? (
                  <span className="public-site__product-lightbox__count">
                    {safeImageIndex + 1} / {gallery.length}
                  </span>
                ) : null}
              </p>
              {gallery.length > 1 ? (
                <div
                  ref={lightboxThumbsRef}
                  className="public-site__product-lightbox__thumbs"
                  role="tablist"
                  aria-label={t('site.productGallery')}
                >
                  {gallery.map((src, i) => (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      role="tab"
                      aria-selected={i === safeImageIndex}
                      className={`public-site__product-lightbox__thumb${i === safeImageIndex ? ' public-site__product-lightbox__thumb--active' : ''}`}
                      onClick={() => selectGalleryIndex(i)}
                    >
                      <img src={resolveMediaSrc(src)} alt="" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>,
          document.body,
        )
        : null}
    </article>
  );
}
